import {
    type AdminAgentNoteRead,
    type components,
} from '@libraries/typescript/api_client';

type AgentNoteItemOutcome = components['schemas']['AgentNoteItemOutcome'];

type EntityChip = {
    readonly key: string;
    readonly name: string;
    readonly entityType: 'caregiver' | 'client';
};

/** The note's linked people as display chips (caregiver, client). */
export function entityChips(note: AdminAgentNoteRead): Array<EntityChip> {
    const chips: Array<EntityChip> = [];
    if (note.caregiver) {
        chips.push({
            key: `caregiver:${note.caregiver.id}`,
            name: note.caregiver.name,
            entityType: 'caregiver',
        });
    }
    if (note.client) {
        chips.push({
            key: `client:${note.client.id}`,
            name: note.client.name,
            entityType: 'client',
        });
    }
    return chips;
}

// Items a human already ruled on; bulk reclassify skips notes carrying
// one, so their checkboxes would be dead weight. Mirrors
// _OPERATOR_PARKED_FALLBACK_REASONS on the server.
const PARKED_FALLBACK_REASONS = new Set([
    'proposal_rejected',
    'outreach_filter_mode_conflict',
    'playbook_edit_rejected',
]);

/**
 * Whether the bulk reclassify endpoint would act on this note: it is
 * retryable from scratch (server-computed is_retryable), or an item is held
 * FAILED and not parked behind a human decision.
 */
export function noteHasReclassifiableWork(note: AdminAgentNoteRead): boolean {
    return (
        note.is_retryable ||
        note.items.some(
            (item) =>
                item.status === 'failed' &&
                !PARKED_FALLBACK_REASONS.has(item.fallback_reason ?? ''),
        )
    );
}

export type FeedStatusChip = {
    readonly key: string;
    readonly label: string;
    readonly tone: 'neutral' | 'positive' | 'warning' | 'critical';
};

const OUTCOME_CHIP_TONES: Record<AgentNoteItemOutcome, FeedStatusChip['tone']> =
    {
        applied: 'positive',
        proposed: 'neutral',
        draft_created: 'neutral',
        conflict_notified: 'warning',
        fallback_memory: 'warning',
        unroutable: 'warning',
        failed: 'critical',
    };

const OUTCOME_SHORT_LABELS: Record<AgentNoteItemOutcome, string> = {
    applied: 'applied',
    proposed: 'proposed',
    draft_created: 'draft',
    conflict_notified: 'conflict',
    fallback_memory: 'fallback',
    unroutable: 'unroutable',
    failed: 'failed',
};

/**
 * Compact status chips for a feed card: classifier state for notes without
 * items ("awaiting classifier", "classify failed ×N"), otherwise the item
 * count plus per-outcome tallies ("2 items · 1 applied · 1 failed").
 */
export function feedStatusChips(
    note: AdminAgentNoteRead,
): Array<FeedStatusChip> {
    if (note.status === 'pending') {
        return [
            { key: 'pending', label: 'awaiting classifier', tone: 'neutral' },
        ];
    }
    if (note.status === 'failed' && note.items.length === 0) {
        const attempts =
            note.classify_attempts > 1 ? ` ×${note.classify_attempts}` : '';
        return [
            {
                key: 'classify-failed',
                label: `classify failed${attempts}`,
                tone: 'critical',
            },
        ];
    }
    const chips: Array<FeedStatusChip> = [
        {
            key: 'items',
            label: `${note.items.length} item${note.items.length === 1 ? '' : 's'}`,
            tone: 'neutral',
        },
    ];
    const outcomeCounts = new Map<AgentNoteItemOutcome, number>();
    for (const item of note.items) {
        if (item.outcome) {
            outcomeCounts.set(
                item.outcome,
                (outcomeCounts.get(item.outcome) ?? 0) + 1,
            );
        }
    }
    for (const [outcome, count] of outcomeCounts) {
        chips.push({
            key: `outcome:${outcome}`,
            label: `${count} ${OUTCOME_SHORT_LABELS[outcome]}`,
            tone: OUTCOME_CHIP_TONES[outcome],
        });
    }
    const pendingItems = note.items.filter((item) => !item.outcome).length;
    if (pendingItems > 0) {
        chips.push({
            key: 'items-pending',
            label: `${pendingItems} pending`,
            tone: 'neutral',
        });
    }
    return chips;
}
